import {useState, useCallback, useLayoutEffect} from "react";
import ResizeObserver from 'resize-observer-polyfill';
import throttle from "js/common/utils/throttle";

const useDimensions = ({throttleDelayInMillis = 50} = {}) => {
  const [dimensions, setDimensions] = useState({});
  const [node, setNode] = useState(null);

  const ref = useCallback(node => setNode(node), []);

  useLayoutEffect(() => {
    if (node) {
      const measure = () => window.requestAnimationFrame(() => {
        const {width, height} = node.getBoundingClientRect();
        // getBoundingClientRect is zoom-scaled, but callers size content in the node's own
        // CSS pixels, so an ancestor `zoom` would inflate every derived height. currentCSSZoom
        // is 1 when nothing sets zoom, making this a no-op for every existing caller; the
        // `|| 1` also covers browsers that do not implement it (added in Chrome 128).
        const cssZoom = node.currentCSSZoom || 1;
        setDimensions({width: width / cssZoom, height: height / cssZoom});
      });
      let throttledMeasure;
      if (throttleDelayInMillis) {
        throttledMeasure = throttle(measure, throttleDelayInMillis);
      } else {
        throttledMeasure = measure;
      }
      throttledMeasure();

      const resizeObserver = new ResizeObserver(() => {
        throttledMeasure(node);
      });
      resizeObserver.observe(node);

      window.addEventListener("resize", throttledMeasure);
      return () => {
        window.removeEventListener("resize", throttledMeasure);
        resizeObserver.disconnect();
      };
    }
  }, [node, throttleDelayInMillis]);

  return [ref, dimensions, node];
};

export default useDimensions;
